import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault } from 'jotai/utils';
import { useCallback } from 'react';

import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { customFieldsByIdMapLoadable } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { kpiDefinitionByKeyMapAtomLoadable } from '../../../services/state/KPIConfigState';
import { columnsByPathStateLoadable, selectedViewIdPFJ } from '../state/ViewStateJ';
import { useSaveMprStateInView } from './mprViewActions';
import { mpReportingGridApi } from './MultiPeriodGrid';
import {
  createMprViewSettings,
  IMprViewSettings,
  MprColumnConfig,
  MprColumnSortType,
} from './MultiPeriodReporting.schemas';
import { useMprConfigToColDefs } from './multiperiodReportingGridData';
import {
  getColumnConfigKey,
  mprColumnNameComparator,
  multiPeriodDateComparator,
} from './multiPeriodReportingUtils';

export const savedMprViewConfigAtom = atomFamily(() => atom<IMprViewSettings | null>(null));

const draftColumnConfigsAtom = atomFamily((viewId: number) =>
  atomWithDefault<MprColumnConfig[]>((get) => get(savedMprViewConfigAtom(viewId))?.columns ?? [])
);
const draftColumnSortConfigsAtom = atomFamily((viewId: number) =>
  atomWithDefault<MprColumnSortType | null>((get) => get(savedMprViewConfigAtom(viewId))?.columnSort ?? null)
);
const sortedAndDedupedDraftColumnsAtom = atomFamily((viewId: number) =>
  atom<MprColumnConfig[]>((get) => {
    const columns = get(draftColumnConfigsAtom(viewId));
    const draftSortType = get(draftColumnSortConfigsAtom(viewId));
    const uniqueColumns = Array.from(
      columns
        .reduce(
          (map, config) => map.set(getColumnConfigKey(config), config),
          new Map<string, MprColumnConfig>()
        )
        .values()
    );
    const kpiConfigMap = get(kpiDefinitionByKeyMapAtomLoadable);
    const columnsByPath = get(columnsByPathStateLoadable(COMPANY_VIEW_TYPE.RETURN_FORECAST));
    const customFieldsById = get(customFieldsByIdMapLoadable);
    if (
      kpiConfigMap.state !== 'hasData' ||
      columnsByPath.state !== 'hasData' ||
      customFieldsById.state !== 'hasData'
    ) {
      return uniqueColumns;
    }
    switch (draftSortType) {
      case MprColumnSortType.dateAsc:
        return uniqueColumns
          .toSorted((a, b) =>
            mprColumnNameComparator(a, b, kpiConfigMap.data, columnsByPath.data, customFieldsById.data)
          )
          .sort(multiPeriodDateComparator);
      case MprColumnSortType.displayName:
        return uniqueColumns
          .toSorted(multiPeriodDateComparator)
          .sort((a, b) =>
            mprColumnNameComparator(a, b, kpiConfigMap.data, columnsByPath.data, customFieldsById.data)
          );
      default:
        return uniqueColumns;
    }
  })
);

export function useMprColumnConfigs() {
  const updateView = useSaveMprStateInView();
  const viewId = useAtomValue(selectedViewIdPFJ);
  const draftColumns = useAtomValue(sortedAndDedupedDraftColumnsAtom(viewId));
  const setDraftColumns = useSetAtom(draftColumnConfigsAtom(viewId));
  const [draftSortType, setDraftSortType] = useAtom(draftColumnSortConfigsAtom(viewId));
  const configToColDefs = useMprConfigToColDefs();

  const [saved, saveConfigs] = useAtom(savedMprViewConfigAtom(viewId));
  const gridApi = useAtomValue(mpReportingGridApi);

  const saveViewConfigs = useCallback(
    async (updateGridColDefs = true) => {
      // save current sort order for custom sort - will be ignored otherwise
      const columnsWithSortOrder = draftColumns.map((config, i) => ({ ...config, sortOrder: i }));
      const newConfig: Pick<IMprViewSettings, 'columns' | 'columnSort'> = {
        columns: columnsWithSortOrder,
        columnSort: draftSortType,
      };

      saveConfigs((prev) => ({
        ...(prev ?? createMprViewSettings()),
        ...newConfig,
      }));

      if (updateGridColDefs) {
        gridApi?.setGridOption('columnDefs', configToColDefs(columnsWithSortOrder, true));
      }
      updateView({ multiPeriodReportingConfig: newConfig });
    },
    [draftColumns, draftSortType, saveConfigs, updateView, gridApi, configToColDefs]
  );

  const resetDrafts = useCallback(() => {
    setDraftColumns(saved?.columns ?? []);
    setDraftSortType(saved?.columnSort ?? null);
  }, [saved, setDraftColumns, setDraftSortType]);

  return {
    draftColumnConfigs: draftColumns,
    setDraftColumnConfigs: setDraftColumns,
    draftColumnSortType: draftSortType,
    setDraftColumnSortType: setDraftSortType,
    saveConfigState: saveViewConfigs,
    resetDrafts,
  };
}
